const getAnalyticsEventBus = require('../common/getAnalyticsEventBus');
const { siblings } = require('../common/utils/dom');

const analyticsEventBus = getAnalyticsEventBus();

const functionalAnd = (fn1, fn2) => (param) => fn1(param) && fn2(param);
const functionalOr = (fn1, fn2) => (param) => fn1(param) || fn2(param);

const firstChildMatchesAny =
  (...selectors) =>
  (parent) => {
    let matches = false;
    const firstChild = parent.firstElementChild;
    for (let i = 0; i < selectors.length; i++) {
      if (firstChild !== null) {
        matches = firstChild.matches(selectors[i]);
      }
      if (matches) break;
    }
    return matches;
  };

const tailChildrenMatches = (parentSelector) => {
  const selector = `${parentSelector}:not(:first-child)`;
  return (parent) => {
    const count = parent.childElementCount;
    let matches = false;
    if (count > 1) {
      matches = count - 1 === parent.querySelectorAll(selector).length;
    }
    return matches;
  };
};

const hasSelectPlaceholder = functionalOr(
  firstChildMatchesAny(':empty', '[label=""]'),
  functionalAnd(
    firstChildMatchesAny(':not([value])', '[value=""]'),
    tailChildrenMatches('[value]')
  )
);

const queryLabel = (formControl) =>
  formControl.nextElementSibling &&
  formControl.nextElementSibling.matches('label');

const queryFormGroup = (formControl) => formControl.closest('.form-group');

const addLabelToSelectPlaceholderIfNeeded = (select) => {
  const placeholder = select.firstElementChild;
  let label;
  if (
    firstChildMatchesAny(':empty', '[label=""]')(select) ||
    placeholder.textContent.trim() === ''
  ) {
    label = queryLabel(select);
    label = label && label.textContent;
  } else {
    label = placeholder.textContent;
  }
  if (label && label !== '') {
    placeholder.textContent = '';
    placeholder.setAttribute('label', label);
    placeholder.setAttribute('value', '');
  }
};

const addPlaceholderToSelect = (select, label) => {
  const option = document.createElement('option');
  option.setAttribute('label', label);
  option.setAttribute('value', '');
  return select.insertBefore(option, select.firstElementChild);
};

/**
 * Adds or remove a given class.
 * @param {Element} node Element to change classlist
 * @param {boolean} shouldAdd true if class has to be added
 * @param {string} classname css class to add or remove
 * @returns {undefined}
 */
function addOrRemoveClass(node, shouldAdd, classname) {
  return node.classList[shouldAdd ? 'add' : 'remove'](classname);
}

/**
 * Check if default option is empty
 * @param {Element} node Element to check options
 * @returns {undefined}
 */
function checkValueIsEmpty(node) {
  let isEmpty = false;
  if (node.tagName === 'SELECT' && node.options.length > 0) {
    // As all selects are forced to have a first placeholder element
    isEmpty = node.selectedIndex === 0;
  } else {
    isEmpty = node.value === '';
  }
  return isEmpty;
}

/**
 * Render the error (or errors) related to a form field
 *
 * @param {HTMLElement|jQuery} fieldControl - The control element (input, select
 * or textarea)
 * @param {string|string[]} errorMsg - The error/s message/s
 * @returns {void}
 */
function addFieldError(fieldControl, errorMsg) {
  const $fieldControl = $(fieldControl);
  let errorMessage = errorMsg;
  if (Array.isArray(errorMessage)) {
    errorMessage = errorMessage.join('<br/>');
  }

  if ($fieldControl.length === 0 || !errorMessage) return;

  const $fieldLabel = $fieldControl.siblings('label');
  const $invalidFeedback = $fieldControl.siblings('.invalid-feedback');

  $fieldControl.addClass('is-invalid');
  $invalidFeedback.html(errorMessage);
  if ($invalidFeedback.hasClass('transition-ap8')) {
    $invalidFeedback.addClass('transition-ap8--show');
  }

  const target = $fieldControl.get(0);
  const form = $fieldControl.closest('form').get(0);
  const targetLabel = $fieldLabel.text().trim();
  analyticsEventBus.publish(analyticsEventBus.EVENTS.formFieldErrorShown, {
    errorMessage,
    formName: form?.name,
    name: target.name,
    label: targetLabel,
    pageType: gaInfo.pageType,
  });
}

/**
 * Render the general error (or errors) after a form validation.
 *
 * @param {HTMLFormElement|jQuery} form - The form
 * @param {string|string[]} errorMsg - The error/s message/s
 * @returns {void}
 */
function addFormError(form, errorMsg) {
  // jquery closest check the element itself too
  const $form = $(form).closest('form');

  let errorMessage = errorMsg;
  if (Array.isArray(errorMessage)) {
    errorMessage = errorMessage.join('<br/>');
  }

  if ($form.length === 0 || !errorMessage) return;

  const $formFeedback = $form.find('.form-invalid-feedback');
  if ($formFeedback.length > 0) {
    $formFeedback.html(errorMessage);
  } else {
    $form.prepend(
      `<p class="form-invalid-feedback" role="alert">${errorMessage}</p>`
    );
  }

  const target = $form.get(0);
  analyticsEventBus.publish(analyticsEventBus.EVENTS.formErrorShown, {
    errorMessage,
    formName: target.name,
    pageType: gaInfo.pageType,
  });
}

/**
 * Clear the error from a form field.
 * @param {HTMLElement} fieldControl The form control element
 * (input/select/textarea)
 * @param {HTMLElement} [invalidFeedback] The element that shows
 *  the error associated to the first param
 */
function clearError(fieldControl, invalidFeedback) {
  if (!fieldControl) {
    return;
  }
  const safeInvalidFeedback =
    invalidFeedback || siblings(fieldControl, '.invalid-feedback')[0];
  fieldControl.classList.remove('is-invalid');
  if (safeInvalidFeedback) {
    safeInvalidFeedback.textContent = '';
  }
}

/**
 * It finds the first invalid input in the form, gets its position relative to the top of the page, and
 * scrolls to that position minus the height of the header
 * @param {HTMLFormElement} form - The form element that you want to validate.
 */
const scrollToError = (form) => {
  const errorSelector = form.querySelector('.is-invalid');
  if (!errorSelector) return;
  const { top } = errorSelector.getBoundingClientRect();
  const headerHeight =
    parseInt(
      document.documentElement.style
        .getPropertyValue('--header-total-height')
        .slice(0, -2),
      10
    ) || 40; // default header height
  window.scrollTo(0, top + window.scrollY - headerHeight);
};

/**
 * Get a map which associates a select options with another selects related options
 * @returns {Object<string, Object[]>[]} state-comunas map
 */
function getTwoSelectsMap(mainSelector, dependantSelector) {
  const dependantOptions =
    module.exports.getOptionsFromSelect(dependantSelector);
  const mainOptions = module.exports.getOptionsFromSelect(mainSelector);
  return mainOptions.reduce((result, mainOption) => {
    if (mainOption.value) {
      // eslint-disable-next-line no-param-reassign
      result[mainOption.value] = dependantOptions.filter(
        (dependantOption) =>
          dependantOption.value.indexOf(mainOption.value) === 0
      );
    }
    return result;
  }, {});
}

/**
 * Query associated selector for a given select.
 * @param {Element} mainSelect - Main select element
 * @returns {Element} - Associated select
 */
function queryAssociatedSelect(mainSelect, associatedSelector) {
  let container = mainSelect.closest('fieldset');
  if (!container) {
    container = mainSelect.closest('form');
  }
  return container.querySelector(associatedSelector);
}

/**
 * Check if select should have placeholder or not
 * @param {Element} select Select node
 * @return {boolean} True if current select should have placeholder
 */
function shouldHavePlaceholder(select) {
  return !select.classList.contains('form-control--no-placeholder');
}

module.exports = {
  addDelegatedListeners() {
    const self = this;
    $(document).on('change', '.form-control', (event) => {
      self.modifyFormGroupState(event.currentTarget);
    });
  },

  addFieldError,
  addFormError,
  clearError,
  /**
   * Clear all the errors from a form
   * @param {HTMLFormElement|jQuery} form The form to clear
   */
  clearFormErrors(form) {
    const formElement = form instanceof jQuery ? form[0] : form;
    if (!formElement) {
      return;
    }
    // Fields' errors
    const invalidFields = formElement.querySelectorAll(
      '.form-control.is-invalid'
    );
    invalidFields.forEach((field) => {
      clearError(field);
    });
    // Main error
    formElement
      .querySelectorAll('.form-invalid-feedback')
      .forEach((invalidFeedback) => {
        invalidFeedback.parentNode.removeChild(invalidFeedback);
      });
  },
  scrollToError,

  /**
   * Get an array of options from a select.
   *
   * @param {string} selector
   * @returns {Object[]} options from targeted select
   */
  getOptionsFromSelect(selector) {
    const select = document.querySelector(selector);
    if (!select) return [];
    return Array.from(select.options);
  },

  init() {
    this.addDelegatedListeners();
    $(() => {
      require('./ds/forms/country-prefix-selector').init();
    });
    $(() => {
      require('./ds/forms/region-selector').init();
    });
    this.initializeSelects();
  },

  initializeSelects() {
    const selectNodes = document.querySelectorAll('select.form-control');
    const numNodes = selectNodes.length;
    for (let i = 0; i < numNodes; i++) {
      const select = selectNodes[i];
      // Add placeholder
      if (shouldHavePlaceholder(select)) {
        if (!hasSelectPlaceholder(select)) {
          const label = queryLabel(select);
          const labelText = label && label.textContent ? label.textContent : '';
          addPlaceholderToSelect(select, labelText.trim());
        } else {
          addLabelToSelectPlaceholderIfNeeded(select);
        }
      }
      // Set state css classes
      this.modifyFormGroupState(select);
    }
  },

  /**
   * @deprecated probably since 54f91f7e (see input-state mixin or :blank)
   */
  modifyFormGroupState(formControl) {
    const isEmpty = checkValueIsEmpty(formControl);
    const isFilled = !isEmpty;
    addOrRemoveClass(formControl, isFilled, 'is-filled');
    addOrRemoveClass(formControl, isEmpty, 'is-empty');
    const formGroup = queryFormGroup(formControl);
    if (formGroup === null) return; // There ar
    addOrRemoveClass(formGroup, isFilled, 'is-filled');
    addOrRemoveClass(formGroup, isEmpty, 'is-empty');
  },

  /**
   * Copies the field values from a formA to another formB
   * @param  {jQuery} formAFields - jQuery object with all the fields to copy from the formA
   * @param  {jQuery} formBBlock - jQuery object containing the formB fields
   * @param  {string} formBName - Name contained by every formB fields name attribute
   * @param  {boolean} copyIfFilled - True if wanted to copy also to the previously filled formB fields
   */
  copyFormValuesToAnotherForm(
    formAFields,
    formBBlock,
    formBName,
    copyIfFilled
  ) {
    // Sort fields in order to copy them with the corresponding options loaded
    // in dependant selects
    const sortedFields = formAFields.sort((a, b) => {
      // common fields
      if (!a.dataset.dependant && !a.dataset.dependsOn) {
        // a should be before
        return -1;
      }
      if (!b.dataset.dependant && !b.dataset.dependsOn) {
        // b should be before
        return 1;
      }

      // main select before dependants
      if (a.dataset.dependant && !a.dataset.dependsOn) {
        return -1;
      }
      if (b.dataset.dependant && !b.dataset.dependsOn) {
        return 1;
      }

      // dependant selects
      if (a.dataset.dependsOn === b.id) {
        return 1;
      }
      if (b.dataset.dependsOn === a.id) {
        return -1;
      }
      return 0;
    });
    for (let i = 0; i < sortedFields.length; i++) {
      const field = sortedFields[i];
      const fieldName = $(field).attr('name').split('_').pop();
      const formBField = $(
        `[name*="_${formBName}_"][name$="${fieldName}"]:not([data-autocomplete-form=off])`,
        formBBlock
      );

      if ((!copyIfFilled && formBField.val() === '') || copyIfFilled) {
        formBField.val($(field).val());
        if (formBField.attr('data-dependant')) {
          formBField.change(); // Trigger change event (dependant select updating)
        }
      }
    }
  },

  /**
   * Enables selector options with ids matching the given prefix and disables every other option.
   * Used for relationship between "comuna" and "state" selectors.
   * @param {jQuery} selector - jQuery object with the selector node.
   * @param {string} prefix - The prefix that select option ids must match. Example: 'CL-AT'
   */
  makeModifyDependantSelect(associatedOptionsMap) {
    return (dependantSelect, primarySelectValue = '') => {
      if (!dependantSelect) return;

      const dependantSelectValue = dependantSelect.value; // dependantSelect.querySelector('option:selected')?.value;

      const newOptions = document.createDocumentFragment();
      const hasPlaceholder = shouldHavePlaceholder(dependantSelect);
      const placeholder = new Option('', '', true, dependantSelectValue === '');

      if (!hasPlaceholder) {
        placeholder.disabled = true;
        placeholder.defaultSelected = false;
        placeholder.selected = false;
      }

      newOptions.appendChild(placeholder);

      let selectedOptionFound = dependantSelectValue === '';
      associatedOptionsMap[primarySelectValue]?.forEach((comuna, index) => {
        selectedOptionFound =
          selectedOptionFound || comuna.value === dependantSelectValue;
        const option = new Option(
          comuna.text,
          comuna.value,
          !hasPlaceholder && index === 0,
          comuna.value === dependantSelectValue
        );
        option.classList.add('field-select__option');
        newOptions.appendChild(option);
      });
      const hasSelectedOptionChanged = !selectedOptionFound;
      // eslint-disable-next-line no-param-reassign
      dependantSelect.innerHTML = '';
      dependantSelect.appendChild(newOptions);

      if (hasSelectedOptionChanged) {
        $(dependantSelect).change();
      }
    };
  },

  /**
   * Show only dependantSelect options which are related to the mainSelect selected option
   * @param  {string} mainSelectSelector - Selector of the mainSelect.
   * @param  {string} dependantSelectSelector - Selector of the dependantSelect.
   */
  associateSelects(mainSelectSelector, dependantSelectSelector) {
    const relationshipMap = getTwoSelectsMap(
      mainSelectSelector,
      dependantSelectSelector
    );
    const modifyDependantSelect =
      module.exports.makeModifyDependantSelect(relationshipMap);
    const $mainSelects = $(mainSelectSelector);
    $mainSelects.each(function fillRelationalData() {
      const mainSelect = $(this).get(0);
      const dependantSelect = queryAssociatedSelect(
        mainSelect,
        dependantSelectSelector
      );
      if (dependantSelect) {
        dependantSelect.dataset.dependsOn = mainSelect.id;
        mainSelect.dataset.dependant = dependantSelect.id;
      }
    });
    $mainSelects
      .on('change', function handleMainSelectChange() {
        const dependantSelect = queryAssociatedSelect(
          $(this).get(0),
          dependantSelectSelector
        );
        if (dependantSelect) {
          const mainSelectedValue = $(this).val();
          modifyDependantSelect(dependantSelect, mainSelectedValue);
        }
      })
      .trigger('change');
  },
};
